// ============================================
// chargeMasters.js
// Pure helpers for the admin-managed charge masters:
//   order.payment_gateway_charges  → gateway fee rules
//   master.shipping_charges        → shipping options + weight slabs
// Used by checkoutCalculations.js, usePaymentPendingCalculations.js,
// ShippingRatesDisplay.jsx and the create-payment-link API route, so the
// browser and the server always compute the same numbers.
// ============================================

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// ────────────────────────────────────────────
// Gateway charges
// ────────────────────────────────────────────

// Legacy hard-coded rules — only used when the master was not passed in
// (e.g. before the query has loaded). Same numbers as the seed rows.
const legacyGatewayCharge = ({ gatewayName, baseOrderTotal, isINR, isUSD, conversionRate, usdConversionRate }) => {
  const name = (gatewayName || '').toLowerCase();
  if (!name) return 0;
  if (name === 'cod' && isINR) return baseOrderTotal <= 5000 ? 100 : baseOrderTotal * 0.02;
  if (name.includes('paypal') && !isINR) return baseOrderTotal * 0.07;
  if (name === 'local bank transfer' && !isINR) return baseOrderTotal * 0.02;
  if (name.includes('yesbank') && !isINR) {
    if (isUSD) return 25;
    return 25 * (usdConversionRate / conversionRate);
  }
  if (name.includes('hdfc')) return baseOrderTotal * (isINR ? 0.02 : 0.03);
  return 0;
};

const scopeMatches = (scope, isINR) => {
  const s = (scope || 'ALL').toUpperCase();
  if (s === 'ALL') return true;
  if (s === 'INR') return isINR;
  if (s === 'NON_INR') return !isINR;
  return false;
};

// Picks the rule that applies to this gateway / currency / order amount.
// Amount bounds are in INR: applies when min_order_inr < totalINR <= max_order_inr
// (a null bound is open). Specific scopes (INR / NON_INR) win over ALL.
export const findGatewayRule = ({ rules, gatewayId, gatewayName, baseOrderTotal, isINR, conversionRate = 1 }) => {
  if (!Array.isArray(rules) || !rules.length) return null;
  const name = (gatewayName || '').trim().toLowerCase();
  const totalINR = num(baseOrderTotal) * (num(conversionRate) || 1);

  const candidates = rules.filter((r) => {
    if (r.is_active === false) return false;
    const sameGateway =
      (gatewayId && r.gateway_id === gatewayId) ||
      (name && (r.payment_gateway?.name || '').trim().toLowerCase() === name);
    if (!sameGateway) return false;
    if (!scopeMatches(r.currency_scope, isINR)) return false;
    if (r.min_order_inr != null && !(totalINR > num(r.min_order_inr))) return false;
    if (r.max_order_inr != null && !(totalINR <= num(r.max_order_inr))) return false;
    return true;
  });

  if (!candidates.length) return null;
  const rank = (r) => ((r.currency_scope || 'ALL').toUpperCase() === 'ALL' ? 1 : 0);
  return [...candidates].sort((a, b) => rank(a) - rank(b))[0];
};

// Returns the gateway charge in the SELECTED currency.
// rules === undefined/null → legacy fallback, rules === [] → no charge.
export const calculateGatewayChargeFromMaster = ({
  rules,
  gatewayId = null,
  gatewayName,
  baseOrderTotal,
  isINR,
  isUSD,
  conversionRate = 1,
  usdConversionRate,
}) => {
  if (!gatewayName && !gatewayId) return 0;

  if (!Array.isArray(rules)) {
    return legacyGatewayCharge({ gatewayName, baseOrderTotal, isINR, isUSD, conversionRate, usdConversionRate });
  }

  const rule = findGatewayRule({ rules, gatewayId, gatewayName, baseOrderTotal, isINR, conversionRate });
  if (!rule) return 0;

  const value = num(rule.value);
  if ((rule.charge_type || '').toUpperCase() === 'PERCENT') {
    return num(baseOrderTotal) * (value / 100);
  }

  // FIXED: value is in the rule's currency → INR → selected currency
  const ruleRate = num(rule.currency?.conversion_rate) || 1;
  return (value * ruleRate) / (num(conversionRate) || 1);
};

// ────────────────────────────────────────────
// Shipping charges
// ────────────────────────────────────────────

// slabs: [{ upto_grams: 100, charge: 20 }, { upto_grams: 1000, charge: 30 }]
// upto_grams null/'' = no upper limit.
// Above the last slab: lastCharge + ceil((w - lastUpto) / step) * stepCharge.
// No slabs at all: ceil(w / step) * stepCharge.
export const calculateShippingRowCharge = (row, weightGrams) => {
  const w = Math.max(0, num(weightGrams));
  const step = num(row?.extra_step_grams);
  const stepCharge = num(row?.extra_step_charge);
  const slabs = (Array.isArray(row?.slabs) ? row.slabs : [])
    .map((s) => ({
      upto: s?.upto_grams === '' || s?.upto_grams == null ? Infinity : num(s.upto_grams),
      charge: num(s?.charge),
    }))
    .sort((a, b) => a.upto - b.upto);

  const hit = slabs.find((s) => w <= s.upto);
  if (hit) return hit.charge;

  const last = slabs[slabs.length - 1];
  const baseW = last ? last.upto : 0;
  const baseCharge = last ? last.charge : 0;
  if (step > 0) return baseCharge + Math.ceil((w - baseW) / step) * stepCharge;
  return baseCharge;
};

// Row amount (row currency) → selected currency
export const toSelectedCurrency = (amount, row, selectedCurrency) => {
  const rowRate = num(row?.currency?.conversion_rate) || 1;
  const selRate = num(selectedCurrency?.conversion_rate) || 1;
  return (num(amount) * rowRate) / selRate;
};

// Builds the list of options to show for a zone.
// subtotalINR decides free shipping (free_above_amount is in the row currency).
export const buildShippingOptions = ({ rows, zone, weightGrams, subtotalINR, selectedCurrency }) => {
  const active = (rows || [])
    .filter((r) => r.is_active !== false && (r.zone || '').toUpperCase() === zone)
    .filter((r) => r.max_weight_grams == null || num(weightGrams) <= num(r.max_weight_grams))
    .sort((a, b) => num(a.sort_order) - num(b.sort_order));

  const options = active.map((r) => {
    const rowRate = num(r.currency?.conversion_rate) || 1;
    const subtotalInRowCurrency = num(subtotalINR) / rowRate;
    const isFree = r.free_above_amount != null && subtotalInRowCurrency >= num(r.free_above_amount);
    const chargeRow = isFree ? 0 : calculateShippingRowCharge(r, weightGrams);
    return {
      row: r,
      id: r.code,
      label: r.label,
      delivery: r.delivery_text || '',
      description: r.description || '',
      icon: r.icon || 'truck',
      isFree,
      chargeRow,
      chargeDisplay: toSelectedCurrency(chargeRow, r, selectedCurrency),
      rowCurrencyName: (r.currency?.name || '').toUpperCase(),
      rowSymbol: r.currency?.symbol || '',
    };
  });

  const anyFree = options.some((o) => o.isFree);
  return anyFree ? options.filter((o) => o.isFree || !o.row.hide_when_free) : options;
};
